import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DrawerModule } from 'primeng/drawer';

import { ChatConversacionesListaComponent } from '../chat-conversaciones-lista/chat-conversaciones-lista';
import { ChatwootNotificationService } from 'src/app/shared/services/chatwoot-notification.service';
import { CoreService } from 'src/app/shared/services/core.service';
import { SStorageService } from 'src/app/shared/services/sessionStorage.service';

/**
 * Botón flotante (FAB) global de chats de WhatsApp, montado en el layout (full.component):
 * visible SOLO en el portal Nodo (idPortal '3', mismo mecanismo que el header) con sesión activa.
 * - Badge: total de no leídos de la campanita (watched + bandeja global solo-mías).
 * - Click → panel lateral derecho con la lista de conversaciones en modo compacto.
 * - Click en una conversación → abre el chat drawer (openByPhone) y cierra el panel.
 */
@Component({
    selector: 'app-chat-global-fab',
    standalone: true,
    imports: [DrawerModule, ChatConversacionesListaComponent],
    templateUrl: './chat-global-fab.html',
    styleUrl: './chat-global-fab.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatGlobalFabComponent {
    private readonly notificationService = inject(ChatwootNotificationService);
    private readonly coreService = inject(CoreService);
    private readonly sstorageService = inject(SStorageService);

    /** Portal activo (misma señal que usa el header para el icono de Chat global). */
    private readonly idPortal = this.coreService.getIdPortalSignal();

    /** Panel lateral de conversaciones abierto/cerrado. */
    readonly panelVisible = signal(false);

    /**
     * FAB visible: portal Nodo + sesión activa (mismo gate de token que el polling de la
     * campanita: SStorageService 'token') + chat habilitado para el rol (admin-chat).
     * Se reevalúa con cada cambio de portal (login / cambio de portal), que es cuando
     * la sesión puede aparecer o desaparecer.
     */
    readonly mostrarFab = computed(() => {
        if (this.idPortal() !== '3') return false;
        if (!this.notificationService.chatAcceso.chatHabilitado()) return false;
        const token = this.sstorageService.getItem('token');
        return !!token && String(token).length > 0;
    });

    /** Badge del FAB: mismo total de la campanita para Chatwoot (watched + bandeja solo-mías). */
    readonly totalNoLeidos = computed(
        () => this.notificationService.totalUnread() + this.notificationService.inboxTotalUnread()
    );

    constructor() {
        // "← Volver al listado" desde el chat drawer: reabre el panel; la lista se
        // hidrata del snapshot (ChatGlobalPanelStateService) sin recargar.
        this.notificationService.reabrirPanelFab
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.panelVisible.set(true));
    }

    abrirPanel(): void {
        this.panelVisible.set(true);
    }

    /** El chat drawer se abre ENCIMA; el panel se cierra para no apilar overlays. */
    onConversacionAbierta(): void {
        this.panelVisible.set(false);
    }
}
